import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Card from 'app/frontend/base/components/Card'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'
import { useCharacterClasses } from 'app/frontend/base/hooks/useCharacterClasses'
import { presentParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useMemo } from 'react'
import { participantColor } from '../../TeamEventOverview/utils/participantColor'
import { Separator } from '@/components/ui/separator'
import { usePageInfo } from 'app/frontend/base/config/queries'

type Props = {
  event: wowaudit.Event
}

const EventAttendanceSummary = ({ event }: Props) => {
  const characterClasses = useCharacterClasses()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const presentParticipants = useMemo(
    () =>
      event.participants.filter((participant) =>
        presentParticipantStatuses.includes(participant.status),
      ),
    [event.participants],
  )
  const presentCountByClass = useMemo(() => {
    const countByClass = new Map<CharacterClass, number>()

    presentParticipants.forEach((participant) => {
      const characterClass = participant.primaryCharacterReference
        .characterClass as CharacterClass
      if (!characterClasses.includes(characterClass)) return
      countByClass.set(
        characterClass,
        (countByClass.get(characterClass) ?? 0) + 1,
      )
    })

    return characterClasses.map((characterClass) => ({
      characterClass,
      count: countByClass.get(characterClass) ?? 0,
    }))
  }, [presentParticipants, characterClasses])

  return (
    <Card className='flex flex-col items-center gap-2 p-2 max-sm:w-full sm:flex-row sm:items-center sm:gap-3 sm:py-1 sm:px-2'>
      <span className='font-medium max-sm:text-base text-sm'>
        <span className={participantColor(event, gameVersion)}>
          {presentParticipants.length}
        </span>
        <span className='text-muted-foreground px-1'>/</span>
        <span className='text-muted-foreground'>
          {event.participants.length}
        </span>
        <span className='sm:hidden ml-2 text-muted-foreground'>present</span>
      </span>

      <Separator orientation='vertical' className='h-4 max-sm:hidden' />
      <Separator className='sm:hidden' />

      <div className='flex flex-wrap items-center gap-x-3 gap-y-1 max-sm:w-full max-sm:justify-center max-sm:gap-2'>
        {presentCountByClass.map(({ characterClass, count }) => (
          <span
            key={characterClass}
            className='inline-flex items-center gap-1 text-sm'
          >
            <Tooltip>
              <TooltipTrigger>
                <CharacterClassIcon
                  characterClass={characterClass}
                  size='small'
                />
              </TooltipTrigger>
              <TooltipContent>{characterClass}</TooltipContent>
            </Tooltip>
            <span className={count === 0 ? 'text-warning-foreground' : ''}>
              {count}
            </span>
          </span>
        ))}
      </div>
    </Card>
  )
}

export default EventAttendanceSummary
